import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Preferences } from "@capacitor/preferences";
import { PendingSyncIndicator } from "@/components/PendingSyncIndicator";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Check, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import StepProgress from "./StepProgress";
import Step1Pin from "./steps/Step1Pin";
import Step2CustomerInfo from "./steps/Step2CustomerInfo";
import Step3Consent from "./steps/Step3Consent";
import Step4DamageType from "./steps/Step4DamageType";
import Step5Photos from "./steps/Step5Photos";
import Step6Scope from "./steps/Step6Scope";
import Step7Moisture from "./steps/Step7Moisture";
import Step8FloorPlan from "./steps/Step8FloorPlan";
import Step9Equipment from "./steps/Step9Equipment";
import Step10Hazards from "./steps/Step10Hazards";
import Step11Inventory from "./steps/Step11Inventory";
import Step12Debrief from "./steps/Step12Debrief";
import Step13Estimate from "./steps/Step13Estimate";
import Step14Paperwork from "./steps/Step14Paperwork";
import Step15SignOff from "./steps/Step15SignOff";

const PLACEHOLDER_LABELS = {
  8: "Floor Plan Sketch", 9: "Equipment Placement", 10: "Hazard Identification",
  11: "Material & Contents Inventory", 12: "Customer Debrief", 13: "Estimate Summary",
  14: "Paperwork & Signature", 15: "Sign-off & Submission",
};

export default function InspectionWizard() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const hasBundle = useRef(false);

  const fetchWorkflow = useCallback(async () => {
    try {
      const { data } = await API.get(`/inspection/${requestId}`);
      setBundle(data);
      hasBundle.current = true;
      Preferences.set({ key: `fpq_inspection_${requestId}`, value: JSON.stringify(data) });
      const wf = data?.workflow;
      if (wf?.current_step) setCurrentStep(wf.current_step);
      setLoading(false);
    } catch (e) {
      const cached = !e?.response && (await Preferences.get({ key: `fpq_inspection_${requestId}` })).value;
      if (!e?.response && hasBundle.current) {
        // offline mid-inspection: keep the in-memory copy
      } else if (cached) {
        setBundle(JSON.parse(cached));
        hasBundle.current = true;
        toast.info("Offline: showing your last synced copy. Changes will sync when you're back online.");
      } else {
        toast.error(e?.response?.data?.detail || "Could not load inspection");
      }
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => { if (requestId) fetchWorkflow(); }, [requestId, fetchWorkflow]);

  const onAdvance = (next) => {
    if (next >= 1 && next <= 15) setCurrentStep(next);
    fetchWorkflow();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }
  if (!bundle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
          <p className="text-sm text-zinc-700">Could not open inspection.</p>
          <Button onClick={() => navigate("/vendor/active")} className="mt-3" data-testid="back-to-active">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Active Jobs
          </Button>
        </div>
      </div>
    );
  }

  const { workflow, request } = bundle;
  const stepData = workflow[`step_${currentStep}`] || {};

  const renderStep = () => {
    const common = { workflow, request, requestId, onAdvance, fetchWorkflow };
    switch (currentStep) {
      case 1: return <Step1Pin {...common} stepData={stepData} />;
      case 2: return <Step2CustomerInfo {...common} stepData={stepData} />;
      case 3: return <Step3Consent {...common} stepData={stepData} />;
      case 4: return <Step4DamageType {...common} stepData={stepData} />;
      case 5: return <Step5Photos {...common} stepData={stepData} />;
      case 6: return <Step6Scope {...common} stepData={stepData} />;
      case 7: return <Step7Moisture {...common} stepData={stepData} />;
      case 8: return <Step8FloorPlan {...common} stepData={stepData} />;
      case 9: return <Step9Equipment {...common} stepData={stepData} />;
      case 10: return <Step10Hazards {...common} stepData={stepData} />;
      case 11: return <Step11Inventory {...common} stepData={stepData} />;
      case 12: return <Step12Debrief {...common} stepData={stepData} />;
      case 13: return <Step13Estimate {...common} stepData={stepData} />;
      case 14: return <Step14Paperwork {...common} stepData={stepData} />;
      case 15: return <Step15SignOff {...common} stepData={stepData} />;
      default:
        return (
          <div className="p-6 text-center">
            <p className="text-sm text-zinc-700 font-semibold mb-1">
              Step {currentStep}: {PLACEHOLDER_LABELS[currentStep]}
            </p>
            <p className="text-xs text-zinc-500 mb-4">Coming in the next phase of the build.</p>
            <Button onClick={() => setCurrentStep(currentStep - 1)} variant="outline" data-testid={`step-${currentStep}-back`}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-24" data-testid="inspection-wizard">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-zinc-900 text-white px-4 py-3 flex items-center justify-between shadow-md">
        <Button size="sm" variant="ghost" onClick={() => navigate("/vendor/active")}
                className="text-white hover:bg-white/10 -ml-2" data-testid="back-to-active">
          <ArrowLeft className="w-4 h-4 mr-1" /> Exit
        </Button>
        <div className="text-center flex-1">
          <p className="text-[10px] uppercase tracking-wider text-zinc-300">Restoration Estimate Inspection</p>
          <p className="text-sm font-semibold truncate">{request.request_code} · {request.customer_name}</p>
        </div>
        <PendingSyncIndicator onSynced={fetchWorkflow} />
      </header>

      <StepProgress workflow={workflow} currentStep={currentStep} onJump={setCurrentStep} />

      <main className="p-4 max-w-2xl mx-auto">
        {renderStep()}
      </main>

      {/* Bottom step navigator */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 px-4 py-2 flex items-center justify-between gap-2 z-30">
        <Button size="sm" variant="outline" disabled={currentStep <= 1}
                onClick={() => setCurrentStep(currentStep - 1)}
                className="flex-1 h-11" data-testid="wizard-prev">
          <ArrowLeft className="w-4 h-4 mr-1" /> Previous
        </Button>
        <div className="text-xs font-bold text-zinc-700 px-2 min-w-[60px] text-center">
          Step {currentStep}/15
        </div>
        <Button size="sm"
                onClick={() => setCurrentStep(Math.min(15, currentStep + 1))}
                disabled={
                  currentStep >= 15 ||
                  (workflow.steps[String(currentStep)]?.status !== "complete" &&
                   workflow.steps[String(currentStep)]?.status !== "na")
                }
                className="flex-1 h-11 bg-zinc-900 hover:bg-zinc-800 text-white disabled:bg-zinc-200 disabled:text-zinc-400"
                data-testid="wizard-next">
          Next <Check className="w-4 h-4 ml-1" />
        </Button>
      </nav>
    </div>
  );
}
